import { TranslateService } from '@ngx-translate/core';
import { CallState, LoopPhase, RunPathEntry, StepResultKind } from '../../models/test-run.model';
import { PathSegment, pathSegments, segmentBadge, segmentText } from './step-path';

/** Was die Anordnung ueber eine Zeile wissen muss; Ergebnis, Detail und Live-Liste liefern es aus ihren Typen. */
export interface ResultNode {
  id: string;
  parentId: string | null;
  kind: StepResultKind;
  callState: CallState | null | undefined;
  passIndex: number | null | undefined;
  loopPhase: LoopPhase | null | undefined;
  /** Datenblattzeile des Aufrufs; der Server laesst das Feld ohne Zeile weg. */
  iterationIndex?: number | null;
}

/** Gruppenkopf, Schleifenkopf, Runde, Block Rückweg, Runde im Rückweg, nicht ausgefuehrter Aufruf, Schritt. */
export type ResultRowKind = 'group' | 'loop' | 'pass' | 'return' | 'returnPass' | 'call' | 'step';

export interface ResultRow<T> {
  item: T;
  kind: ResultRowKind;
  /** Ebene fuer den Einzug, 0 … {@link MAX_LEVEL}; tiefer sieht aus wie die letzte Ebene. */
  level: number;
  /** Nur am Schleifenkopf: Zahl der Runden. */
  rounds: number | null;
  /** Runde an `pass` und `returnPass`. */
  pass: number | null;
  /** An `pass` und `returnPass`: die Datenblattzeile, fuer die die Schleife aufgerufen wurde. */
  row: number | null;
  /** Die Zeilen darueber von aussen nach innen; ist eine davon zugeklappt, ist diese Zeile verborgen. */
  headerIds: readonly string[];
}

export const MAX_LEVEL = 4;

/** Ein Fall gilt als neu, sobald eine Zeile einen Aufruf-Zustand traegt; sonst bleibt die alte Darstellung. */
export function isStructured<T>(items: readonly T[], node: (item: T) => ResultNode): boolean {
  return items.some((item) => node(item).callState != null);
}

function kindOf(n: ResultNode, children: readonly ResultNode[]): ResultRowKind | null {
  if (n.kind !== 'MODULE') return 'step';
  if (n.loopPhase === 'ROUND') return 'pass';
  if (n.loopPhase === 'RETURN') return n.passIndex == null ? 'return' : 'returnPass';
  if (n.callState === 'DECLINED' || n.callState === 'OPEN') return 'call';
  if (children.some((child) => child.loopPhase === 'ROUND')) return 'loop';
  // Der ausgefuehrte Selbstaufruf einer Runde: die naechste Runde haengt neben der Runde, nicht darunter.
  if (n.callState === 'EXPANDED' && children.length === 0) return null;
  return 'group';
}

/** Die Datenblattzeile am Schleifenaufruf, also am naechsten Kopf ueber der Runde, der selbst keine Runde ist. */
function loopRowOf(headerIds: readonly string[], byId: ReadonlyMap<string, ResultNode>): number | null {
  const loop = [...headerIds]
    .reverse()
    .map((id) => byId.get(id))
    .find((n) => n?.loopPhase == null);
  return loop?.iterationIndex ?? null;
}

/**
 * Ordnet die Zeilen eines Falls (Lauf-Reihenfolge, Eltern vor Kindern) fuer die Ergebnis- und
 * Live-Ansichten (NEX-2282): ein Kopf je Modulaufruf, Runden und Rückweg als eigene Koepfe,
 * ein nicht ausgefuehrter Aufruf als eine Zeile, leere Gruppen fallen weg.
 */
export function resultRows<T>(items: readonly T[], node: (item: T) => ResultNode): ResultRow<T>[] {
  const nodes = items.map(node);
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const ids = new Set(byId.keys());
  const children = new Map<string, ResultNode[]>();
  for (const n of nodes) {
    if (n.parentId !== null && ids.has(n.parentId)) {
      (children.get(n.parentId) ?? children.set(n.parentId, []).get(n.parentId)!).push(n);
    }
  }
  const ancestors = new Map<string, readonly string[]>();
  const rows: ResultRow<T>[] = [];
  nodes.forEach((n, index) => {
    const parent = n.parentId !== null && ids.has(n.parentId) ? n.parentId : null;
    const headerIds = parent === null ? [] : [...(ancestors.get(parent) ?? []), parent];
    ancestors.set(n.id, headerIds);
    const own = children.get(n.id) ?? [];
    const kind = kindOf(n, own);
    if (kind === null) return;
    rows.push({
      item: items[index],
      kind,
      level: Math.min(headerIds.length, MAX_LEVEL),
      rounds: kind === 'loop' ? own.filter((child) => child.loopPhase === 'ROUND').length : null,
      pass: kind === 'pass' || kind === 'returnPass' ? (n.passIndex ?? null) : null,
      row: kind === 'pass' || kind === 'returnPass' ? loopRowOf(headerIds, byId) : null,
      headerIds,
    });
  });
  return rows;
}

/** Ist eine der Kopfzeilen darueber zugeklappt? */
export function isHidden(row: ResultRow<unknown>, collapsed: ReadonlySet<string>): boolean {
  return row.headerIds.some((id) => collapsed.has(id));
}

/** Pfad eines Gruppenkopfs: die umschliessenden Aufrufe mit Runde und Zeile, dann das Modul selbst. */
export interface HeaderPath {
  parts: string[];
  last: string;
}

export function headerPath(
  path: readonly RunPathEntry[] | null | undefined,
  title: string | null | undefined,
  translate: TranslateService,
): HeaderPath {
  const own: PathSegment = { kind: 'module', title: title?.trim() || null, round: null, row: null };
  const segments = [...pathSegments(null, null, path).slice(1), own];
  const texts = segments.map((segment) => segmentText(segment, translate));
  return { parts: texts.slice(0, -1), last: texts[texts.length - 1] };
}

/** Kopf einer Runde: „Runde 2", mit Datenblattzeile „Runde 2 · Zeile 4"; der Block selbst heisst „Rückweg". */
export function passLabel(
  row: { kind: ResultRowKind; pass: number | null; row: number | null },
  translate: TranslateService,
): string {
  if (row.kind === 'return') return translate.instant('runPath.returnBlock');
  return segmentBadge({ kind: 'module', title: null, round: { phase: 'ROUND', n: row.pass }, row: row.row }, translate);
}
